
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const LuxuryPropertiesPage = () => {
  const [selectedService, setSelectedService] = useState('');
  const [formData, setFormData] = useState({
    propertyValue: '',
    propertyType: '',
    customerName: '',
    email: '',
    phone: '',
    propertyAddress: '',
    specialRequests: ''
  });

  const luxuryServices = [
    {
      id: 'luxury-photography',
      name: 'Luxury Photography Package',
      price: 599,
      features: [
        '40-60 professional images',
        'Drone aerial photography',
        'Twilight/golden hour shots',
        'Interior & exterior coverage',
        'Professional editing & retouching',
        '24-48 hour delivery',
        'High-resolution & web-optimized files'
      ]
    },
    {
      id: 'premium-luxury',
      name: 'Premium Luxury Experience',
      price: 899,
      features: [
        '60-80 professional images',
        'Drone aerial photography',
        'Twilight & sunrise sessions',
        'Virtual staging included',
        'Professional video walkthrough',
        'Same-day delivery available',
        'Dedicated luxury specialist',
        'Multiple angle variations'
      ],
      popular: true
    },
    {
      id: 'ultra-luxury',
      name: 'Ultra-Luxury Showcase',
      price: 1299,
      features: [
        '80-120 professional images',
        'Cinematic drone footage',
        'Multiple twilight sessions',
        'Virtual staging & 3D tours',
        'Professional video production',
        'Lifestyle photography',
        'Same-day rush delivery',
        'Personal project manager',
        'Unlimited revisions'
      ]
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedPkg = luxuryServices.find(service => service.id === selectedService);
    if (!selectedPkg) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-payment-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          packageType: selectedPkg.name,
          price: selectedPkg.price,
          customerName: formData.customerName,
          email: formData.email,
          phone: formData.phone,
          propertyType: formData.propertyType,
          location: formData.propertyAddress,
          propertyValue: formData.propertyValue,
          specialRequests: formData.specialRequests,
          serviceType: 'luxury_photography'
        })
      });

      const { url } = await response.json();
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Error creating payment session:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <div 
        className="pt-20 pb-24 bg-cover bg-center bg-no-repeat relative"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/7c3cd204fa7d49db42ee03d35c68acd2.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center text-white">
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Luxury Property
              <span className="text-yellow-400"> Photography</span>
            </h1>
            <p className="text-xl lg:text-2xl mb-8 max-w-4xl mx-auto">
              Showcase your high-end vacation rental with stunning professional photography that attracts premium guests and commands top-tier pricing.
            </p>
            <div className="flex flex-wrap justify-center gap-8 text-lg">
              <div className="flex items-center">
                <i className="ri-camera-line text-yellow-400 mr-3 text-2xl"></i>
                <span>Professional Equipment</span>
              </div>
              <div className="flex items-center">
                <i className="ri-flight-takeoff-line text-yellow-400 mr-3 text-2xl"></i>
                <span>Drone Photography</span>
              </div>
              <div className="flex items-center">
                <i className="ri-time-line text-yellow-400 mr-3 text-2xl"></i>
                <span>24-Hour Delivery</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Luxury Portfolio Gallery */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">Luxury Portfolio</h2>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            See how we transform luxury properties into irresistible booking magnets with our premium photography services.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-lg shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Luxury%20villa%20living%20room%20with%20floor%20to%20ceiling%20windows%20modern%20furniture%20marble%20floors%20high%20ceilings%20designer%20decor%20natural%20light%20elegant%20interior%20design&width=400&height=300&seq=luxury-1&orientation=landscape"
                  alt="Luxury Villa Interior"
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                  <i className="ri-eye-line text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-4">Modern Luxury Villa</h3>
              <p className="text-gray-600">$2,500/night • 6 bedrooms</p>
            </div>
            
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-lg shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Luxury%20penthouse%20terrace%20with%20infinity%20pool%20city%20skyline%20view%20outdoor%20furniture%20sunset%20lighting%20high%20end%20vacation%20rental%20rooftop%20paradise&width=400&height=300&seq=luxury-2&orientation=landscape"
                  alt="Penthouse Terrace"
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                  <i className="ri-eye-line text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-4">Penthouse with Pool</h3>
              <p className="text-gray-600">$1,800/night • 4 bedrooms</p>
            </div>
            
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-lg shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Luxury%20beachfront%20property%20master%20bedroom%20with%20ocean%20view%20king%20bed%20elegant%20furniture%20sliding%20doors%20to%20balcony%20resort%20style%20interior&width=400&height=300&seq=luxury-3&orientation=landscape"
                  alt="Beachfront Master Suite"
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                  <i className="ri-eye-line text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-4">Beachfront Estate</h3>
              <p className="text-gray-600">$3,200/night • 8 bedrooms</p>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Luxury Photography Packages</h2>
          <div className="grid lg:grid-cols-3 gap-8">
            {luxuryServices.map((service) => (
              <div 
                key={service.id}
                className={`relative bg-white rounded-lg shadow-xl border-2 transition-all cursor-pointer ${
                  selectedService === service.id ? 'border-yellow-500 shadow-2xl' : 'border-gray-200 hover:border-yellow-300'
                } ${service.popular ? 'ring-2 ring-yellow-200' : ''}`}
                onClick={() => setSelectedService(service.id)}
              >
                {service.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-500 text-black px-4 py-1 rounded-full text-sm font-bold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.name}</h3>
                  <div className="text-4xl font-bold text-yellow-600 mb-6">
                    ${service.price}
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <i className="ri-check-line text-yellow-600 mr-3 mt-0.5"></i>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="flex items-center justify-center">
                    <div className={`w-6 h-6 rounded-full border-2 ${
                      selectedService === service.id ? 'bg-yellow-500 border-yellow-500' : 'border-gray-300'
                    } flex items-center justify-center`}>
                      {selectedService === service.id && (
                        <i className="ri-check-line text-black text-sm"></i>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Booking Form */}
      {selectedService && (
        <div className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-white rounded-lg shadow-xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Book Your Luxury Photography</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Property Value Range *
                    </label>
                    <select
                      name="propertyValue"
                      required
                      value={formData.propertyValue}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="">Select value range</option>
                      <option value="500k-1m">$500K - $1M</option>
                      <option value="1m-2m">$1M - $2M</option>
                      <option value="2m-5m">$2M - $5M</option>
                      <option value="5m+">$5M+</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Property Type *
                    </label>
                    <select
                      name="propertyType"
                      required
                      value={formData.propertyType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="">Select property type</option>
                      <option value="luxury-villa">Luxury Villa</option>
                      <option value="penthouse">Penthouse</option>
                      <option value="estate">Estate</option>
                      <option value="mansion">Mansion</option>
                      <option value="resort-property">Resort Property</option>
                      <option value="waterfront">Waterfront Property</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="customerName"
                      required
                      value={formData.customerName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Property Address *
                    </label>
                    <input
                      type="text"
                      name="propertyAddress"
                      required
                      value={formData.propertyAddress}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                      placeholder="Enter complete property address"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Special Requirements or Requests
                  </label>
                  <textarea
                    name="specialRequests"
                    rows={4}
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                    placeholder="Any specific shots, timing preferences, or special requirements..."
                    maxLength={500}
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Max 500 characters</p>
                </div>
                
                <div className="bg-yellow-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Luxury Package Summary</h3>
                  <div className="flex justify-between items-center">
                    <span>{luxuryServices.find(s => s.id === selectedService)?.name}</span>
                    <span className="font-bold text-xl">${luxuryServices.find(s => s.id === selectedService)?.price}</span>
                  </div>
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-yellow-500 text-black py-4 px-6 rounded-lg font-bold hover:bg-yellow-600 transition-colors whitespace-nowrap text-lg"
                >
                  Book Luxury Photography Session
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Why Choose Us */}
      <div className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold mb-12 text-center">Why Luxury Hosts Choose Us</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-line text-3xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Premium Results</h3>
              <p className="text-gray-300">Our luxury photography increases booking rates by 60% and allows 35% higher nightly rates for high-end properties.</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-star-line text-3xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Luxury Specialists</h3>
              <p className="text-gray-300">Our photographers specialize in high-end properties and understand how to showcase luxury amenities and architectural details.</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-3xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">White-Glove Service</h3>
              <p className="text-gray-300">Dedicated project managers, flexible scheduling, and same-day delivery options to meet your luxury standards.</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LuxuryPropertiesPage;
